import React, { useState } from 'react';
import { useLocation } from '@reach/router';
import { Box, Flex, IconButton, Tag } from '@chakra-ui/react';
import { Home20, Menu32, Account20, ChartBar20 } from '@carbon/icons-react';
import { camelCase } from 'change-case';

import DataPointsLogoText from './DataPointsLogoText';
import SidebarGroup from './SidebarGroup';
import { SidebarLink } from './SidebarTriggers';
import { useProducts } from '#core/Products';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { usePermissions } from '#core/Permissions';
import { useInstitution } from '#core/Institution';
import { useEnvironment } from '#core/Environment';

// JME
// import { useAuthHook } from '#utils/useAuthHook';
// JME1
import { useAuth } from '#core/Auth';
import { SIDEBAR_WIDTH } from './constants';
import Sparkle20 from '#core/Dashboard/assets/SparkleIcon';

const MenuIcon = () => <Menu32 />;
const Sidebar: React.FC = () => {
  const flags = useFlags();
  const { reputationUrl, theInsightsUrl } = useEnvironment();
  const { canUseElevatedPermissions, useElevatedPermissions }: any = useAuth(); // JME2
  const location = useLocation();
  const { products } = useProducts();
  const { hasResourcePermissionNew } = usePermissions();
  const { institution } = useInstitution();
  const { insightsPreviewUrl } = institution;

  const hasReputationCoreRole = hasResourcePermissionNew({
    action: 'reputation_core_new:view_tableau_dashboard',
    institutionId: institution.institutionId,
  });

  const hasReputationAdvancedRole = hasResourcePermissionNew({
    action: 'reputation_advanced_new:view_tableau_dashboard',
    institutionId: institution.institutionId,
  });

  const hasTheInsightsRole = hasResourcePermissionNew({
    action: 'the_insights:view_dashboard',
    institutionId: institution.institutionId,
  });

  const canViewFreeWurDashboard = hasResourcePermissionNew({
    year: new Date().getFullYear(),
    action: 'dataplus:viewPageFree',
    institutionId: institution.institutionId,
  });

  const [, , productPath] = location.pathname.split('/');
  const productUserIsWithin = products.find(
    (product) => product.path === productPath,
  );

  const [openGroup, setOpenGroup] = useState<string | null>(
    productUserIsWithin?.name || null,
  );

  const [isExpanded, setIsExpanded] = useState(false);

  const mobileAndTabletDisplay = isExpanded ? 'flex' : 'none';

  const showNewReputation = flags[camelCase('NewReputation')];

  const showTheInsights = flags[camelCase('showTheInsightsMenuOption')];

  const showWurDashboardPreview = flags[camelCase('showWurDashboardPreview')];

  return (
    <>
      <Flex
        /* if this changes the width of LegacyIFrame needs to change */
        width={{ base: '100vw', lg: SIDEBAR_WIDTH }}
        bg={useElevatedPermissions ? 'red.700' : 'white'}
        minH="64px"
        align="center"
        px={{ base: 2, lg: 5 }}
      >
        <IconButton
          icon={<MenuIcon />}
          color="gray.900"
          bg={useElevatedPermissions ? 'red.700' : 'white'}
          _hover={{ bg: useElevatedPermissions ? 'red.800' : 'gray.100' }}
          _active={{ bg: useElevatedPermissions ? 'red.900' : 'gray.200' }}
          aria-label="Open side bar menu"
          onClick={() => setIsExpanded(!isExpanded)}
          data-testid="menu"
          display={{ base: 'inline-flex', lg: 'none' }}
        />
        <DataPointsLogoText
          canUseElevatedPermissions={canUseElevatedPermissions}
          useElevatedPermissions={useElevatedPermissions}
        />
      </Flex>
      <Flex
        w={{ base: 240, md: SIDEBAR_WIDTH }}
        borderRight={{ base: 3, lg: 'none' }}
        borderRightColor="yellow.500"
        borderRightStyle="solid"
        bottom={0}
        bg="white"
        flexDirection="column"
        justifyContent="flex-start"
        h="calc(100vh - 64px)"
        overflowY="auto"
        overflowX="hidden"
        display={{
          base: mobileAndTabletDisplay,
          lg: 'flex',
        }}
      >
        <Flex as="ul" listStyleType="none" flexDirection="column" flexGrow={1}>
          <SidebarLink
            icon={Home20}
            to="/datapoints/"
            isActive={location.pathname === '/datapoints/'}
          >
            Home
          </SidebarLink>

          {products.map((product) => {
            if (product.productConfig.noChildren) {
              const page = product.pages[0];
              return (
                <SidebarLink
                  key={product.name}
                  icon={product.icon}
                  to={page.url}
                  isActive={location.pathname.includes(`/${product.path}/`)}
                >
                  {product.name}
                </SidebarLink>
              );
            }
            return (
              <SidebarGroup
                key={product.name}
                icon={product.icon}
                label={product.name}
                isOpen={openGroup === product.name}
                userIsWithin={productUserIsWithin?.name === product.name}
                handleToggle={setOpenGroup}
              >
                <Box as="ul" listStyleType="none">
                  {product.pages.map((page) => {
                    if (page.hiddenPage) {
                      return null;
                    }

                    const isActive =
                      page.url === '/'
                        ? location.pathname === '/'
                        : location.pathname.startsWith(page.url);

                    return (
                      <SidebarLink
                        key={page.url}
                        to={page.url}
                        isInGroup
                        isActive={isActive}
                      >
                        {page.name}
                      </SidebarLink>
                    );
                  })}
                </Box>
              </SidebarGroup>
            );
          })}

          {canViewFreeWurDashboard &&
            insightsPreviewUrl &&
            showWurDashboardPreview && (
              <SidebarLink
                icon={ChartBar20}
                to={insightsPreviewUrl}
                isActive={location.pathname.includes('/datapoints/wur/')}
                isExternalLink={true}
              >
                WUR Dashboard
              </SidebarLink>
            )}

          {hasReputationCoreRole && showNewReputation && (
            <Box display="flex">
              <SidebarLink
                icon={Account20}
                to={`${reputationUrl}dashboard/reputation-core/${institution.institutionId}`}
                isExternalLink={true}
              >
                Reputation Core
                <Box marginLeft="8px">
                  <Tag size="sm">NEW</Tag>
                </Box>
              </SidebarLink>
            </Box>
          )}

          {hasReputationAdvancedRole && showNewReputation && (
            <SidebarLink
              icon={Account20}
              to={`${reputationUrl}dashboard/reputation-advanced/${institution.institutionId}`}
              isExternalLink={true}
            >
              Reputation
              <br />
              Advanced
              <Box marginLeft="8px">
                <Tag size="sm">NEW</Tag>
              </Box>
            </SidebarLink>
          )}

          {hasTheInsightsRole && showTheInsights && (
            <SidebarLink
              icon={Sparkle20}
              to={`${theInsightsUrl}/auth/signin`}
              isExternalLink={true}
            >
              THE Insights
            </SidebarLink>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default Sidebar;
